export type WorkflowStepPageCursor = {
  hasMore: boolean;
  cursor: string | null;
  status?: "warming" | "ready";
  coverage?: "warming" | "ready";
  retryAfterSeconds?: number;
};
type StepPageData = {
  pages: readonly WorkflowStepPageCursor[];
  pageParams: readonly unknown[];
};

export function workflowStepHistoryInitialPageParam(
  capturedCursor: string | null | undefined,
  currentHeadCursor: string | null | undefined,
): string | undefined {
  return capturedCursor ?? currentHeadCursor ?? undefined;
}

export function workflowStepsRequestPath(runId: string, source: string, statuses: string, view: "flat" | "tree", parentKey?: string, cursor?: string): string {
  const params = new URLSearchParams({ source, order: "desc", limit: "100" });
  if (statuses) params.set("statuses", statuses);
  if (view === "tree") params.set("view", view);
  if (parentKey) params.set("parentKey", parentKey);
  if (cursor) params.set("cursor", cursor);
  return `/runs/${encodeURIComponent(runId)}/steps?${params}`;
}

export type WorkflowStepPageQueryContext = {
  pageParam: string | undefined;
  signal: AbortSignal;
};

export async function fetchWorkflowStepPageUntilReady<Page extends WorkflowStepPageCursor>(
  fetchPage: () => Promise<Page>,
  waitForRetry: (retryAfterSeconds?: number) => Promise<void>,
): Promise<Page> {
  let page = await fetchPage();
  let warmingAttempts = 0;
  while (page.status === "warming" || page.coverage === "warming") {
    const serverDelay = page.retryAfterSeconds ?? 1;
    const backoffDelay = Math.min(30, 2 ** warmingAttempts);
    await waitForRetry(Math.max(serverDelay, backoffDelay));
    warmingAttempts += 1;
    page = await fetchPage();
  }
  return page;
}

/** Production history options keep cursor pages disabled until the view's sentinel requests them. */
export function workflowStepHistoryQueryOptions<Page extends WorkflowStepPageCursor>({
  queryKey,
  initialPageParam,
  queryFn,
}: {
  queryKey: readonly unknown[];
  initialPageParam: string | undefined;
  queryFn: (context: WorkflowStepPageQueryContext) => Promise<Page>;
}) {
  return {
    queryKey,
    enabled: false,
    initialPageParam,
    queryFn,
    retry: false,
    getNextPageParam: (last: Page, _pages: Page[], _lastPageParam: string | undefined, pageParams: readonly unknown[]) =>
      nextWorkflowStepCursor(last, pageParams),
  };
}

export type WorkflowStepRetryAction = "next-page" | "refetch";

export function shouldAutoLoadWorkflowStepPage({
  enabled,
  hasNextPage,
  isFetching,
  isError,
}: {
  enabled: boolean;
  hasNextPage: boolean;
  isFetching: boolean;
  isError: boolean;
}): boolean {
  // Status filters must not gate this decision; the same sentinel drives every history page.
  return enabled && hasNextPage && !isFetching && !isError;
}

/**
 * A failed infinite-query fetch carries its direction separately from cached
 * pages. Only a confirmed next-page failure with another cursor available can
 * retry that page; initial and refetch failures retry the initial request.
 */
export function workflowStepRetryAction({
  hasNextPage,
  isFetchNextPageError,
  isRefetchError,
}: {
  hasNextPage: boolean;
  isFetchNextPageError: boolean;
  isRefetchError: boolean;
}): WorkflowStepRetryAction {
  return isFetchNextPageError && !isRefetchError && hasNextPage ? "next-page" : "refetch";
}

/** Cursors are opaque. Only follow a non-empty cursor that has not been requested. */
export function nextWorkflowStepCursor(
  page: WorkflowStepPageCursor,
  requestedCursors: readonly unknown[],
): string | undefined {
  const cursor = page.cursor;
  if (!page.hasMore || !cursor?.trim() || requestedCursors.includes(cursor)) return undefined;
  return cursor;
}

/** Infinite scrolling must agree with the last page before requesting a cursor. */
export function hasMoreWorkflowStepPages(data: StepPageData | undefined): boolean {
  if (!data) return false;
  const lastPage = data.pages.at(-1);
  return !!lastPage && nextWorkflowStepCursor(lastPage, data.pageParams) !== undefined;
}
